// Cliente da Admin API, tipado a partir do OpenAPI (risco 11.3: cliente gerado do contrato).
// O navegador fala só com o BFF: cookie de sessão + token CSRF; nunca recebe o token do Keycloak.
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type Paths = paths;
export type Issue = Schemas["Issue"];

export class ApiError extends Error {
  status: number;
  code: string;
  issues: Issue[];
  loginUrl?: string;
  constructor(status: number, code: string, message: string, issues: Issue[] = [], loginUrl?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.issues = issues;
    this.loginUrl = loginUrl;
  }
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | string[]>;
  body?: unknown;
  /** Confirmação digitada com o nome do recurso (RF-ADM-SEG-04). */
  confirm?: string;
  signal?: AbortSignal;
  /** Resposta em texto (CSV, YAML). */
  text?: boolean;
}

export interface ClientOptions {
  base?: string;
  csrf: () => string | undefined;
  /** Caminho atual, para o BFF montar a URL de retorno do login. */
  returnPath?: () => string;
  /** Chamado em session_expired e step_up_required, com a ação pendente. */
  onAuth?: (err: ApiError, pending: PendingRequest) => void;
}

export interface PendingRequest {
  method: string;
  path: string;
  options: RequestOptions;
}

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

function qs(query?: RequestOptions["query"]): string {
  if (!query) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, x));
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? "?" + s : "";
}

export function createClient(opts: ClientOptions) {
  const base = opts.base ?? "";
  async function request<T = unknown>(method: Method, path: string, o: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (o.body !== undefined) headers["Content-Type"] = "application/json";
    if (method !== "GET") {
      const t = opts.csrf();
      if (t) headers["X-CSRF-Token"] = t;
    }
    if (o.confirm) headers["X-Polaris-Confirm"] = o.confirm;
    if (opts.returnPath) headers["X-Polaris-Return"] = opts.returnPath();
    const res = await fetch(base + path + qs(o.query), {
      method,
      headers,
      body: o.body === undefined ? undefined : JSON.stringify(o.body),
      credentials: "same-origin",
      signal: o.signal,
    });
    if (res.status === 204) return undefined as T;
    if (o.text && res.ok) return (await res.text()) as T;
    let data: any = null;
    const ct = res.headers.get("Content-Type") ?? "";
    if (ct.includes("json")) data = await res.json().catch(() => null);
    else if (!res.ok) data = { error: { code: "http_" + res.status, message: await res.text() } };
    if (!res.ok) {
      const e = data?.error ?? {};
      const err = new ApiError(res.status, e.code ?? "error", e.message ?? res.statusText, e.issues ?? [], e.login_url);
      if ((err.code === "session_expired" || err.code === "step_up_required") && opts.onAuth) {
        opts.onAuth(err, { method, path, options: o });
      }
      throw err;
    }
    return data as T;
  }
  return {
    request,
    get: <T = unknown>(path: string, o?: RequestOptions) => request<T>("GET", path, o),
    post: <T = unknown>(path: string, body?: unknown, o?: RequestOptions) => request<T>("POST", path, { ...o, body }),
    put: <T = unknown>(path: string, body?: unknown, o?: RequestOptions) => request<T>("PUT", path, { ...o, body }),
    patch: <T = unknown>(path: string, body?: unknown, o?: RequestOptions) => request<T>("PATCH", path, { ...o, body }),
    del: <T = unknown>(path: string, o?: RequestOptions) => request<T>("DELETE", path, o),
  };
}

export type Client = ReturnType<typeof createClient>;

/** Abre o SSE de eventos da Admin API pelo BFF (RNF-ADM-02). */
export function subscribe(onEvent: (type: string, data: any) => void, base = ""): () => void {
  const es = new EventSource(base + "/api/v1/events/stream", { withCredentials: true });
  const types = ["change", "snapshot", "rollout", "approval", "health", "alert", "revocation", "incident"];
  const handlers = types.map((t) => {
    const h = (ev: MessageEvent) => {
      try {
        onEvent(t, JSON.parse(ev.data));
      } catch {
        /* evento malformado: ignora */
      }
    };
    es.addEventListener(t, h as EventListener);
    return [t, h] as const;
  });
  return () => {
    handlers.forEach(([t, h]) => es.removeEventListener(t, h as EventListener));
    es.close();
  };
}
